import * as React from 'react';
import { useContext, useEffect, useState } from 'react';

import { OneTimeOrderContext } from '.';
import { InlineSpinner } from '../../assets/Spinner';
import { formatingOptions } from '../../formatingOptions';
import { CountryCode, OneTimeModalType, Option } from '../../pages/types';
import {
  defaultShippingOption,
  filterProductsByPetMealType,
  STANDARD_SHIPPING_FOR_FROZEN,
} from '../../utils';
import { Dropdown } from '../Dropdown';
import { MultiSelectDropdown } from '../MultiSelectDropdown';
import { Button } from '../buttons/Button';

export const OneTimeOrderStep1 = () => {
  const {
    dogs,
    setSelectedDog,
    fullProducts,
    selectedProducts,
    setSelectedProducts,
    isLoading,
    isEverythingSelected,
    navigateBetweenSteps,
    selectedDog,
    selectedShippingMethod,
    setSelectedShippingMethod,
    shippingOptions,
    isLoadingShippingRates,
    customer,
  } = useContext(OneTimeOrderContext);
  const formattedProducts = formatingOptions(fullProducts, { value: 'id', label: 'code' });
  const [filteredOptions, setFilteredOptions] = useState(formattedProducts);
  const [showExpeditedShipping, setShowExpeditedShipping] = useState(true);
  const isCanadianCustomer = customer.default_country === CountryCode.CA;

  const getFullProductByCode = (productCode: string) => {
    return fullProducts.find((product) => product.code === productCode);
  };

  const hasAnyFrozenProducts = () => {
    return selectedProducts.some((selectedProduct) => {
      const fullProduct = getFullProductByCode(selectedProduct.label);
      return fullProduct?.is_frozen;
    });
  };

  useEffect(() => {
    const hasAnyFrozenProduct = hasAnyFrozenProducts();
    setShowExpeditedShipping(!hasAnyFrozenProduct);
    if (hasAnyFrozenProduct) {
      setSelectedShippingMethod(STANDARD_SHIPPING_FOR_FROZEN);
    } else {
      const defaultOption = defaultShippingOption(shippingOptions, customer);
      if (defaultOption && selectedShippingMethod === STANDARD_SHIPPING_FOR_FROZEN) {
        setSelectedShippingMethod(defaultOption);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProducts, fullProducts, customer, shippingOptions, hasAnyFrozenProducts]);

  // Filter shipping options to only show STANDARD when frozen/fresh products are present
  const availableShippingOptions = showExpeditedShipping
    ? shippingOptions
    : shippingOptions.filter((option) => option.value === 'STANDARD');
  useEffect(() => {
    setFilteredOptions(
      filterProductsByPetMealType(fullProducts, OneTimeModalType.ONE_TIME_ORDER, selectedProducts)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProducts, fullProducts]);

  const handleSelectProducts = (updatedProducts: Option[]) => {
    setSelectedProducts(updatedProducts);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="w-full pr-2 pt-3 flex flex-col gap-2">
        <div className="text-md font-semibold text-left">
          <h3>Which dog would you like to create an order for?</h3>
        </div>
        <div className="mb-2 max-w-[80%]">
          <Dropdown
            options={dogs}
            selectedOption={selectedDog || { value: '', label: 'Select dog' }}
            setSelectedOption={setSelectedDog}
          />
        </div>
      </div>
      <div className="w-full pr-2 flex flex-col gap-2">
        <div className="text-md font-semibold text-left">
          <h3>Which product(s) should we include in this one-time order?</h3>
        </div>
        <div className="mb-2 flex relative max-w-[80%]">
          <MultiSelectDropdown
            options={filteredOptions}
            selectedOptions={selectedProducts}
            setSelectedOptions={handleSelectProducts}
            placeholder="Start typing a product code"
          />
          {isLoading && (
            <div className="absolute inset-0 bg-black/10 flex items-center justify-center">
              <InlineSpinner />
            </div>
          )}
        </div>
      </div>
      {!isCanadianCustomer ? (
        <div className="w-full pr-2 flex flex-col gap-2">
          <div className="text-md font-semibold text-left">
            <h3>Which shipping method should we apply to this one-time order?</h3>
          </div>
          <div className="mb-2 flex relative max-w-[80%]">
            {isLoadingShippingRates ? (
              <div className="flex items-center justify-center py-4">
                <div className="text-gray-500">Loading shipping options...</div>
              </div>
            ) : (
              <Dropdown
                options={availableShippingOptions}
                selectedOption={selectedShippingMethod}
                setSelectedOption={setSelectedShippingMethod}
                isDisabled={!showExpeditedShipping}
              />
            )}
            {isLoading && (
              <div className="absolute inset-0 bg-black/10 flex items-center justify-center">
                <InlineSpinner />
              </div>
            )}
          </div>
        </div>
      ) : null}
      <div className="mt-[45px] w-fill flex justify-center">
        <Button
          isDisabled={!isEverythingSelected}
          label="Continue"
          variant={isEverythingSelected ? 'primary' : 'secondary'}
          onClick={navigateBetweenSteps}
        />
      </div>
    </div>
  );
};
